import type { Practitioner } from '@medplum/fhirtypes';
import {
  formatPractitionerStaffLabel,
  getCredentials,
  getPractitionerOrganizationRef,
} from '@unify/fhir-model/resources/practitioner';
import { resolvePermissionsFromProfile } from '@/features/rbac';
import type { Role } from '@/pages/users/types/rbac';
import {
  getSupervisorReference,
  getSupervisionStartedAt,
  isActivePractitioner,
  practitionerReferenceId,
} from './supervisionRelationships';

export interface PractitionerSupervisionOption {
  value: string;
  label: string;
  name: string;
  credentials?: string;
  practiceName?: string;
}

export interface SupervisionTableRow {
  id: string;
  name: string;
  credentials?: string;
  practiceName?: string;
  startedAt?: string;
}

export function formatSupervisionStartedAt(startedAt: string | undefined): string {
  if (!startedAt) return '—';
  const parsed = new Date(`${startedAt}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return startedAt;
  return parsed.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

export function practitionerDisplayName(practitioner: Practitioner): string {
  return formatPractitionerStaffLabel(practitioner) || practitioner.id || 'Unknown';
}

export function formatPractitionerSupervisionLabel(
  practitioner: Practitioner,
  practiceName?: string,
): string {
  const name = formatPractitionerStaffLabel(practitioner) || practitioner.id || 'Unknown';
  const credentials = getCredentials(practitioner);
  const parts = [name];
  if (credentials) parts.push(credentials);
  if (practiceName) parts.push(practiceName);
  return parts.join(' · ');
}

export function practitionerHasRole(practitioner: Practitioner, role: Role): boolean {
  return resolvePermissionsFromProfile(practitioner).availableRoles.includes(role);
}

export function practitionerOrganizationId(practitioner: Practitioner): string | undefined {
  return getPractitionerOrganizationRef(practitioner)?.reference?.replace(/^Organization\//, '');
}

export function toSupervisionOption(
  practitioner: Practitioner,
  practiceNameByOrgId: Map<string, string>,
): PractitionerSupervisionOption {
  const orgId = practitionerOrganizationId(practitioner);
  const practiceName = orgId ? practiceNameByOrgId.get(orgId) : undefined;
  const name = practitionerDisplayName(practitioner);
  const credentials = getCredentials(practitioner);
  return {
    value: practitioner.id!,
    name,
    label: formatPractitionerSupervisionLabel(practitioner, practiceName),
    credentials,
    practiceName,
  };
}

export function toSupervisionTableRow(
  practitioner: Practitioner,
  practiceNameByOrgId: Map<string, string>,
): SupervisionTableRow {
  const option = toSupervisionOption(practitioner, practiceNameByOrgId);
  return {
    id: option.value,
    name: option.name,
    credentials: option.credentials,
    practiceName: option.practiceName,
    startedAt: getSupervisionStartedAt(practitioner),
  };
}

export function filterSupervisorCandidates(
  practitioners: Practitioner[],
  options: {
    practiceOrgId?: string;
    unifyScope: boolean;
    excludeId?: string;
  },
): Practitioner[] {
  return practitioners.filter((p) => {
    if (!p.id || p.id === options.excludeId) return false;
    if (!isActivePractitioner(p)) return false;
    if (!practitionerHasRole(p, 'supervisor')) return false;
    if (!options.unifyScope && options.practiceOrgId) {
      return practitionerOrganizationId(p) === options.practiceOrgId;
    }
    return true;
  });
}

export function filterSuperviseeCandidates(
  practitioners: Practitioner[],
  options: {
    practiceOrgId?: string;
    unifyScope: boolean;
    excludeId?: string;
  },
): Practitioner[] {
  return practitioners.filter((p) => {
    if (!p.id || p.id === options.excludeId) return false;
    if (!isActivePractitioner(p)) return false;
    if (!practitionerHasRole(p, 'provider')) return false;
    // Supervisors are never supervised; stacked provider under supervisor is not addable.
    if (practitionerHasRole(p, 'supervisor')) return false;
    if (getSupervisorReference(p)) return false;
    if (!options.unifyScope && options.practiceOrgId) {
      return practitionerOrganizationId(p) === options.practiceOrgId;
    }
    return true;
  });
}

export function getViewedSupervisorId(practitioner: Practitioner): string | undefined {
  return practitionerReferenceId(getSupervisorReference(practitioner));
}
